import type { ImageProps } from 'next/image';
import { getImageProps } from 'next/image';
import type { ImageType } from '../../../interfaces/components';
import type { ImageTeaserType } from './types';

/**
 * ImageTeaser Helper Functions
 *
 * This module provides utilities for optimized teaser image rendering with responsive sizing.
 *
 * SIZING APPROACH:
 * Components accept sizing information from parent containers to avoid hardcoded assumptions.
 * Parent containers (groups, layouts) should provide appropriate sizing based on their context.
 *
 * USAGE EXAMPLES:
 *
 * // Default fallback (responsive)
 * generateOptimizedImageTeaser({ ...params });
 *
 * // Parent provides custom sizes
 * generateOptimizedImageTeaser({
 *   ...params,
 *   sizes: '(min-width: 768px) 50vw, 100vw'
 * });
 *
 * // Grid-aware sizing provided by CmsGroup
 * generateOptimizedImageTeaser({
 *   ...params,
 *   sizes: '(min-width: 768px) 33vw, 100vw'
 * });
 */

// ==================== DATA EXTRACTION ====================

/**
 * Extracts image objects from ImageTeaser data.
 * Returns desktop and mobile image variants.
 */
export const extractImageTeaserObjects = (data: ImageTeaserType) => ({
  imageObjDefault: data.image_default ?? null,
  imageObjMobile: data.image_mobile ?? null,
});

/**
 * Extracts width and height from image data object.
 * Returns null for both if data is invalid or missing.
 */
export const extractImageDimensions = (imageObj: ImageType | null) => {
  if (!imageObj || Array.isArray(imageObj.data)) {
    return { width: null, height: null };
  }
  return {
    width: imageObj.data?.width ? String(imageObj.data.width) : null,
    height: imageObj.data?.height ? String(imageObj.data.height) : null,
  };
};

/**
 * Builds the image source URL, applying optimization transforms if enabled.
 */
export const buildImageTeaserSrc = (
  imageObj: ImageType | null,
  imageOptimizationEnabled: boolean,
  uriTransformSource: RegExp,
  uriTransformEndpoint: string,
) => {
  if (!imageObj?.url) return '';
  if (imageOptimizationEnabled && typeof imageObj?.uri === 'string') {
    return imageObj.uri.replace(uriTransformSource, uriTransformEndpoint);
  }
  return imageObj.url;
};

// ==================== SIZING UTILITIES ====================

/**
 * Generates appropriate sizes string for teaser images based on hints from parent containers.
 */
export const generateImageTeaserSizes = ({ sizes }: { sizes?: string }): string => {
  // Use explicit sizes if provided by parent (e.g., from CmsGroup column context)
  if (sizes) {
    return sizes;
  }

  // Grid-friendly default for unknown context
  return '(min-width: 768px) 50vw, 100vw';
};

// ==================== RENDERING ====================

/**
 * Renders a simple fallback image without optimization.
 * Used for images without dimensions.
 */
export const renderFallbackImageTeaser = (
  imgref: React.RefObject<HTMLImageElement | null>,
  defaultSrc: string,
  altText: string,
  lazyLoading: boolean,
  className?: string,
  style?: React.CSSProperties,
) => (
  <img
    ref={imgref}
    src={defaultSrc}
    alt={altText}
    loading={lazyLoading ? 'lazy' : 'eager'}
    className={className}
    style={style}
  />
);

/**
 * Renders optimized images with responsive sources.
 * Creates a <picture> element if mobile variant exists, otherwise a single <img>.
 */
export const generateOptimizedImageTeaser = (params: {
  defaultSrc: string;
  defaultWidth: string;
  defaultHeight: string;
  mobileSrc: string | null;
  mobileWidth: string | null;
  mobileHeight: string | null;
  canOptimizeMobile: boolean;
  commonImageProps: ImageProps;
  imgref: React.RefObject<HTMLImageElement | null>;
  altText: string;
  className?: string;
  style?: React.CSSProperties;
  sizes?: string;
}) => {
  const {
    defaultSrc,
    defaultWidth,
    defaultHeight,
    mobileSrc,
    mobileWidth,
    mobileHeight,
    canOptimizeMobile,
    commonImageProps,
    imgref,
    altText,
    className,
    style,
    sizes: explicitSizes,
  } = params;

  const sizes = generateImageTeaserSizes({ sizes: explicitSizes });
  // Generate desktop image props
  const {
    props: { srcSet: desktopSrcSet, ...desktopProps },
  } = getImageProps({
    ...commonImageProps,
    src: defaultSrc,
    width: Number.parseInt(defaultWidth, 10),
    height: Number.parseInt(defaultHeight, 10),
    sizes,
  });

  // Generate mobile image props if available
  const mobileImageProps =
    canOptimizeMobile && mobileSrc && mobileWidth && mobileHeight
      ? getImageProps({
          ...commonImageProps,
          src: mobileSrc,
          width: Number.parseInt(mobileWidth, 10),
          height: Number.parseInt(mobileHeight, 10),
          sizes,
        })
      : null;

  // Render with or without mobile variant
  if (mobileImageProps) {
    return (
      <picture>
        <source
          media="(max-width: 600px)"
          srcSet={mobileImageProps.props.srcSet}
          sizes={mobileImageProps.props.sizes}
        />
        <img
          {...desktopProps}
          ref={imgref}
          srcSet={desktopSrcSet}
          className={className}
          style={style}
          alt={altText}
        />
      </picture>
    );
  }

  return (
    <img
      {...desktopProps}
      ref={imgref}
      srcSet={desktopSrcSet}
      className={className}
      style={style}
      alt={altText}
    />
  );
};
